import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, RotateCcw, Eye, Timer, Trophy, Shuffle } from 'lucide-react';
import { Button } from './ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { lanes, tiles, interviewScenarios } from '../data/pipelineData';

// Fisher-Yates shuffle
const shuffleArray = (array) => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

// Draggable Tile Component
const DraggableTile = ({ tile, onDragStart, onDragEnd, onDragOver, onDrop, isDragging, isPlaceholder }) => {
  if (isPlaceholder) {
    return (
      <div 
        className="min-w-[160px] h-[72px] border-2 border-dashed border-[#888780] rounded-lg bg-transparent flex-shrink-0"
        onDragOver={onDragOver}
        onDrop={onDrop}
      />
    );
  }

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, tile)}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, tile)}
      data-tile-id={tile.id}
      data-lane={tile.lane}
      className={`
        min-w-[160px] max-w-[220px] min-h-[72px] p-3 
        bg-white border border-[#D8D5CF] rounded-lg
        flex flex-col justify-center relative
        cursor-grab active:cursor-grabbing select-none
        transition-all duration-150 flex-shrink-0
        hover:shadow-md
        ${isDragging ? 'opacity-45 shadow-lg' : 'opacity-100'}
      `}
    >
      {/* Drag handle */}
      <div className="absolute top-1.5 left-1.5 text-[#B4B2A9] text-xs opacity-0 hover:opacity-100 transition-opacity">
        ⠿⠿
      </div>
      
      <div className="font-mono text-sm font-bold text-[#1A3A5C] mb-0.5">
        {tile.title}
      </div>
      <div className="text-[11px] text-[#5F5E5A] leading-snug">
        {tile.sub}
      </div>
    </div>
  );
};

// Lane Component
const InterviewLane = ({ lane, laneTiles, onDragStart, onDragEnd, onDragOver, onDrop, draggingTile, hardMode, placeholderIndex }) => {
  return (
    <div className="flex items-stretch border-b border-[#E2E8F0] last:border-b-0">
      {/* Lane Label */}
      <div
        className="flex-shrink-0 w-44 p-4 flex items-center border-r border-[#E2E8F0]"
        style={{ backgroundColor: lane.bgColor }}
      >
        <div className="flex items-center gap-3">
          <div
            className={`w-1 h-10 rounded-full ${lane.style === 'dashed' ? 'border border-dashed border-[#888780]' : ''}`}
            style={{ backgroundColor: lane.style !== 'dashed' ? lane.color : 'transparent' }}
          />
          <span className="font-sans font-semibold text-[#1A3A5C] text-sm">
            {lane.name}
          </span>
        </div>
      </div>

      {/* Lane Content */}
      <div
        data-lane={lane.id}
        className={`
          flex-1 flex items-center gap-3 p-4 min-h-[100px]
          transition-all duration-150
          ${draggingTile && (hardMode || draggingTile.lane === lane.id) ? 'bg-[#1D9E75]/5 outline outline-1 outline-dashed outline-[#1D9E75] rounded-lg' : ''}
        `}
        style={{ backgroundColor: lane.bgColor }}
        onDragOver={(e) => onDragOver(e, lane.id)}
        onDrop={(e) => onDrop(e, lane.id)}
      >
        {laneTiles.map((tile, index) => (
          <DraggableTile
            key={tile.id}
            tile={tile}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragOver={(e) => onDragOver(e, lane.id, index)}
            onDrop={(e) => onDrop(e, lane.id, index)}
            isDragging={draggingTile?.id === tile.id}
          />
        ))}
      </div>
    </div>
  );
};

export const InterviewMode = () => {
  const [scenario, setScenario] = useState('scenario1');
  const [hardMode, setHardMode] = useState(false);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showingAnswer, setShowingAnswer] = useState(false);
  const [draggingTile, setDraggingTile] = useState(null);
  
  // Store current tile positions per lane
  const [laneState, setLaneState] = useState(() => {
    const initial = {};
    lanes.forEach(lane => {
      const laneTiles = tiles.filter(t => t.lane === lane.id);
      initial[lane.id] = shuffleArray(laneTiles);
    });
    return initial;
  });

  // Calculate score
  const calculateScore = useCallback(() => {
    let correct = 0;
    let halfCorrect = 0;
    let total = tiles.length;

    lanes.forEach(lane => {
      const currentTiles = laneState[lane.id] || [];
      currentTiles.forEach((tile, currentIdx) => {
        const canonicalTile = tiles.find(t => t.id === tile.id);
        if (!canonicalTile) return;

        const inCorrectLane = canonicalTile.lane === lane.id;
        const inCorrectPos = inCorrectLane && canonicalTile.idx === currentIdx;

        if (inCorrectPos) {
          correct++;
        } else if (inCorrectLane) {
          halfCorrect++;
        }
      });
    });

    const score = correct + halfCorrect * 0.5;
    const percentage = total > 0 ? Math.round((score / total) * 100) : 0;
    return { correct, halfCorrect, total, score, percentage };
  }, [laneState]);

  const scoreData = calculateScore();

  // Timer logic
  useEffect(() => {
    let interval;
    if (isTimerRunning && !showingAnswer) {
      interval = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, showingAnswer]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Drag handlers
  const handleDragStart = (e, tile) => {
    if (showingAnswer) return;
    setDraggingTile(tile);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', tile.id);
  };

  const handleDragEnd = () => {
    setDraggingTile(null);
  };

  const handleDragOver = (e, targetLaneId, insertIndex) => {
    e.preventDefault();
    if (!draggingTile) return;
    
    // Cross-lane restriction unless hard mode
    if (!hardMode && draggingTile.lane !== targetLaneId) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetLaneId, insertIndex) => {
    e.preventDefault();
    if (!draggingTile || showingAnswer) return;
    
    // Cross-lane restriction unless hard mode
    if (!hardMode && draggingTile.lane !== targetLaneId) return;

    setLaneState(prev => {
      const newState = { ...prev };
      
      // Remove from source lane
      const sourceLaneId = draggingTile.lane;
      newState[sourceLaneId] = newState[sourceLaneId].filter(t => t.id !== draggingTile.id);
      
      // Add to target lane
      const targetTiles = [...newState[targetLaneId]];
      const actualIndex = insertIndex !== undefined ? insertIndex : targetTiles.length;
      
      // Update the tile's lane reference for hard mode
      const updatedTile = { ...draggingTile, lane: targetLaneId };
      targetTiles.splice(actualIndex, 0, updatedTile);
      newState[targetLaneId] = targetTiles;
      
      return newState;
    });

    setDraggingTile(null);
  };

  // Actions
  const handleStartPause = () => {
    setIsTimerRunning(!isTimerRunning);
  };

  const handleReset = () => {
    setIsTimerRunning(false);
    setElapsedTime(0);
    setShowingAnswer(false);
    setHardMode(false);
    
    // Reshuffle tiles
    const newState = {};
    lanes.forEach(lane => {
      const laneTiles = tiles.filter(t => t.lane === lane.id);
      newState[lane.id] = shuffleArray(laneTiles);
    });
    setLaneState(newState);
  };

  const handleShowAnswer = () => {
    setIsTimerRunning(false);
    setShowingAnswer(true);
    
    // Animate tiles to correct positions
    const correctState = {};
    lanes.forEach(lane => {
      correctState[lane.id] = tiles
        .filter(t => t.lane === lane.id)
        .sort((a, b) => a.idx - b.idx);
    });
    
    // Stagger animation
    setTimeout(() => {
      setLaneState(correctState);
    }, 100);
  };

  const selectedScenario = interviewScenarios.find(s => s.id === scenario);

  return (
    <div data-testid="interview-mode" className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-[#E8E7E5] rounded-lg border border-[#B4B2A9]">
        {/* Score Section */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#1D9E75]" />
            <span className="font-mono text-sm font-bold text-[#1A3A5C]" data-testid="interview-score">
              {scoreData.score.toFixed(1)} / {scoreData.total} correct ({scoreData.percentage}%)
            </span>
          </div>
          <div className="w-48">
            <Progress value={scoreData.percentage} className="h-2.5" />
          </div>
        </div>

        {/* Timer */}
        <div className="flex items-center gap-3">
          <Timer className="w-5 h-5 text-[#6c757d]" />
          <span className="font-mono text-lg font-bold text-[#1A3A5C] w-16" data-testid="interview-timer">
            {formatTime(elapsedTime)}
          </span>
          <Button
            onClick={handleStartPause}
            variant={isTimerRunning ? 'outline' : 'default'}
            size="sm"
            data-testid="timer-control-btn"
            className={isTimerRunning ? '' : 'bg-[#1D9E75] hover:bg-[#178a65]'}
            disabled={showingAnswer}
          >
            {isTimerRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            <span className="ml-1">{isTimerRunning ? 'Pause' : 'Start'}</span>
          </Button>
        </div>

        {/* Scenario Selector */}
        <div className="flex items-center gap-2">
          <Label htmlFor="scenario" className="text-sm">Scenario:</Label>
          <Select value={scenario} onValueChange={setScenario} disabled={showingAnswer}>
            <SelectTrigger className="w-[200px]" data-testid="scenario-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {interviewScenarios.map(s => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          {!showingAnswer ? (
            <Button
              onClick={handleShowAnswer}
              className="bg-[#378ADD] hover:bg-[#2a6db5]"
              data-testid="show-answer-btn"
            >
              <Eye className="w-4 h-4 mr-1" />
              Show answer
            </Button>
          ) : (
            <Button
              onClick={handleReset}
              variant="outline"
              data-testid="reset-interview-btn"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Reset interview
            </Button>
          )}
          
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded border">
            <Switch
              id="hard-mode"
              checked={hardMode}
              onCheckedChange={setHardMode}
              disabled={showingAnswer}
              data-testid="hard-mode-toggle"
            />
            <Label htmlFor="hard-mode" className={`text-sm ${hardMode ? 'text-[#378ADD] font-semibold' : ''}`}>
              Hard mode
            </Label>
          </div>
        </div>
      </div>

      {/* Scenario Description */}
      {selectedScenario && (
        <div className="text-center text-sm text-[#6c757d]">
          <span className="font-semibold">{selectedScenario.name}:</span>{' '}
          {selectedScenario.description}
          {hardMode && (
            <span className="ml-2 text-[#378ADD] font-semibold">
              (Cross-lane drag enabled)
            </span>
          )}
        </div>
      )}

      {/* Diagram */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden shadow-sm">
        {lanes.map(lane => (
          <InterviewLane
            key={lane.id}
            lane={lane}
            laneTiles={laneState[lane.id] || []}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            draggingTile={draggingTile}
            hardMode={hardMode}
          />
        ))}
      </div>

      {/* Instructions */}
      <div className="text-center text-xs text-[#6c757d]">
        {showingAnswer ? (
          <span className="text-[#1D9E75] font-semibold">
            Showing correct answer. Tiles with green glow are in correct position.
          </span>
        ) : (
          <span>
            Drag tiles to arrange them in the correct order. 
            {hardMode ? ' Hard mode: tiles can be moved between lanes.' : ' Tiles can only be reordered within their lane.'}
          </span>
        )}
      </div>
    </div>
  );
};

export default InterviewMode;
